import { CalendarCheck, CalendarClock, CircleHelp } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import type { ComunicacaoProcessualItem } from '@/api/client';
import { AudienciaBadge } from '@/components/ui/AudienciaBadge';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PrazoBadge, primeiroPrazoParaAgir } from '@/components/ui/PrazoBadge';
import { SeloDeVencimento } from './SeloDeVencimento';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { formatDate, formatNumeroProcesso, toLocalISODate, toTitleCaseClasse } from '@/lib/formatters';
import { formatPartesResumo } from '@/lib/partes/resumo';
import { cn } from '@/lib/utils';

const TIPO_LABELS: Record<string, string> = {
  INTIMACAO: 'Intimação',
  CITACAO: 'Citação',
  NOTIFICACAO: 'Notificação',
  LISTA_DISTRIBUICAO: 'Lista',
  OUTRO: 'Outro',
};

interface ComunicacaoListItemProps {
  com: ComunicacaoProcessualItem;
  onSelect: (id: string) => void;
  /**
   * Quando definido, habilita o botão de cadastrar prazo na agenda (lista
   * completa). Omitido no dashboard, onde a linha é apenas um atalho de leitura.
   */
  onCadastrarPrazo?: (com: ComunicacaoProcessualItem) => void;
  /** Exibe o badge de tipo (Intimação/Citação...). Default: false. */
  showTipo?: boolean;
  /** Densidade compacta (dashboard). Default: false (lista completa). */
  compact?: boolean;
}

/**
 * Linha de uma comunicação processual. Componente único compartilhado pela lista
 * completa (`ComunicacoesPage`) e pelo widget de intimações recentes do dashboard
 * (`RecentComunicacoes`) — evita que as duas telas divirjam no visual e no
 * comportamento (ex.: indicador de prazo já cadastrado na agenda).
 */
export function ComunicacaoListItem({
  com,
  onSelect,
  onCadastrarPrazo,
  showTipo = false,
  compact = false,
}: ComunicacaoListItemProps) {
  const navigate = useNavigate();

  return (
    <div
      className={cn(
        'flex items-center gap-3 cursor-pointer transition-colors hover:bg-accent',
        compact ? 'p-2.5 rounded-lg' : 'p-4',
      )}
      onClick={() => onSelect(com.id)}
    >
      {!com.lida && <div className="w-2 h-2 rounded-full bg-primary shrink-0" />}
      <div className={cn('flex-1 min-w-0', com.lida && 'ml-5')}>
        <p className={cn(
          'break-words text-sm',
          com.lida ? 'text-muted-foreground' : 'font-semibold',
        )}>
          {toTitleCaseClasse(com.assunto ?? com.nome_classe ?? 'Intimação')}
        </p>
        <p className="mt-0.5 break-words text-xs text-muted-foreground">
          {com.sigla_tribunal && <span className="font-medium">{com.sigla_tribunal}</span>}
          {com.sigla_tribunal && ' · '}
          {formatNumeroProcesso(com.numero_processo)}
          {com.data_disponibilizacao && ` — ${formatDate(com.data_disponibilizacao)}`}
        </p>
        {formatPartesResumo(com.destinatarios_json) && (
          <p className="break-words text-xs text-muted-foreground">
            {formatPartesResumo(com.destinatarios_json)}
          </p>
        )}
        {/* De um colega: não se funde com a de quem vê (A7) — diz quem a recebeu. */}
        {com.recebida_por && (
          <p className="text-[11px] text-muted-foreground">Recebida por {com.recebida_por.nome || 'um colega'}</p>
        )}
      </div>

      <AudienciaBadge
        datas={com.datas_extraidas}
        className={com.prazos_extraidos?.length ? 'hidden sm:inline-flex' : undefined}
      />
      {/* Com conta do sistema, a data; sem ela, só o prazo lido no texto. */}
      {com.prazo_calculo ? (
        <SeloDeVencimento calculo={com.prazo_calculo} />
      ) : com.prazo_estado === 'indefinido' && !primeiroPrazoParaAgir(com.prazos_extraidos) ? (
        // Nunca silêncio (plano, decisão 3): sem prazo do texto nem da lei, um marcador
        // discreto — o toque na linha abre o drawer, que diz por quê.
        <Tooltip>
          <TooltipTrigger asChild>
            <span
              role="img"
              tabIndex={0}
              className="shrink-0 cursor-help rounded-sm text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              aria-label="Prazo não identificado"
            >
              <CircleHelp className="h-4 w-4" />
            </span>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-xs">
            <p className="text-xs leading-relaxed">
              Não identificamos o prazo. {com.prazo_presumido?.motivo ?? ''} Confira no tribunal.
            </p>
          </TooltipContent>
        </Tooltip>
      ) : (
        <PrazoBadge prazos={com.prazos_extraidos} />
      )}

      {/* Com o vencimento calculado, o selo basta: todo prazo do sistema já vai para a
          agenda, e o botão dela repetiria a informação. O botão fica para o prazo só
          lido no texto, que se cadastra à mão. */}
      {com.prazo_calculo ? null : com.evento_prazo ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 shrink-0 gap-1 px-2 text-xs text-muted-foreground"
              aria-label="Ver prazo na agenda"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/app/calendario?data=${toLocalISODate(com.evento_prazo!.data_inicio)}`);
              }}
            >
              <CalendarCheck className="h-4 w-4" />
              {new Date(com.evento_prazo.data_inicio).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}
            </Button>
          </TooltipTrigger>
          <TooltipContent>Prazo na agenda — ver no calendário</TooltipContent>
        </Tooltip>
      ) : onCadastrarPrazo && (com.prazos_extraidos?.length ?? 0) > 0 ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 shrink-0"
              aria-label="Cadastrar prazo na agenda"
              onClick={(e) => {
                e.stopPropagation();
                onCadastrarPrazo(com);
              }}
            >
              <CalendarClock className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Cadastrar prazo na agenda</TooltipContent>
        </Tooltip>
      ) : null}

      {showTipo && com.tipo && (
        <Badge variant="outline" className="text-[11px] shrink-0 hidden sm:inline-flex">
          {TIPO_LABELS[com.tipo] ?? com.tipo}
        </Badge>
      )}
    </div>
  );
}
